import { useState, useEffect, useCallback } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', group_ids: [], active: true });

  const load = useCallback(async () => {
    try {
      const [u, g] = await Promise.all([API.get('/users'), API.get('/groups')]);
      setUsers(u.data);
      setGroups(g.data);
    } catch { toast.error('Error cargando usuarios'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', email: '', password: '', group_ids: [], active: true });
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: '', group_ids: u.group_ids || [], active: u.active });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        const data = { ...form };
        if (!data.password) delete data.password;
        await API.put(`/users/${editing.id}`, data);
        toast.success('Usuario actualizado');
      } else {
        if (!form.password) { toast.error('La contrasena es obligatoria'); return; }
        await API.post('/users', form);
        toast.success('Usuario creado');
      }
      setDialogOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (uid) => {
    if (!window.confirm('Eliminar este usuario?')) return;
    try {
      await API.delete(`/users/${uid}`);
      toast.success('Usuario eliminado');
      load();
    } catch { toast.error('Error eliminando'); }
  };

  const toggleGroup = (gid) => {
    setForm(f => ({
      ...f,
      group_ids: f.group_ids.includes(gid) ? f.group_ids.filter(id => id !== gid) : [...f.group_ids, gid]
    }));
  };

  const groupMap = Object.fromEntries(groups.map(g => [g.id, g.name]));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-zinc-50">Usuarios</h1>
          <p className="text-sm text-zinc-500">Gestiona los usuarios del sistema</p>
        </div>
        <Button onClick={openCreate} className="bg-amber-500 hover:bg-amber-600 text-black font-medium" data-testid="create-user-button">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Usuario
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)}
          className="pl-10 bg-zinc-950/50 border-white/10" data-testid="user-search-input" aria-label="Buscar usuarios" />
      </div>

      <div className="rounded-lg border border-white/5 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead className="text-zinc-400">Nombre</TableHead>
              <TableHead className="text-zinc-400">Email</TableHead>
              <TableHead className="text-zinc-400 hidden sm:table-cell">Grupos</TableHead>
              <TableHead className="text-zinc-400 hidden sm:table-cell">Estado</TableHead>
              <TableHead className="text-zinc-400 text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-zinc-500 py-8">No se encontraron usuarios</TableCell></TableRow>
            )}
            {filtered.map(u => (
              <TableRow key={u.id} className="border-white/5" data-testid={`user-row-${u.id}`}>
                <TableCell className="text-zinc-200 font-medium">{u.name}</TableCell>
                <TableCell className="text-zinc-400">{u.email}</TableCell>
                <TableCell className="hidden sm:table-cell">
                  <div className="flex flex-wrap gap-1">
                    {(u.group_ids || []).map(gid => (
                      <Badge key={gid} variant="outline" className="text-amber-500/80 border-amber-500/20 text-xs">
                        {groupMap[gid] || '?'}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="hidden sm:table-cell">
                  <Badge variant={u.active ? 'default' : 'secondary'} className={u.active ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'text-zinc-500'}>
                    {u.active ? 'Activo' : 'Inactivo'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="w-4 h-4 text-zinc-400" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(u.id)} data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 sm:max-w-lg" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 font-heading">{editing ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
            <DialogDescription className="text-zinc-500">{editing ? 'Modifica los datos del usuario' : 'Crea un nuevo usuario en el sistema'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Nombre</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="bg-zinc-950/50 border-white/10" data-testid="user-name-input" aria-label="Nombre" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className="bg-zinc-950/50 border-white/10" data-testid="user-email-input" aria-label="Email" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Contrasena {editing && <span className="text-zinc-600">(dejar vacio para mantener)</span>}</Label>
              <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                className="bg-zinc-950/50 border-white/10" data-testid="user-password-input" aria-label="Contrasena" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Grupos</Label>
              <div className="flex flex-wrap gap-2">
                {groups.map(g => (
                  <button key={g.id} onClick={() => toggleGroup(g.id)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${form.group_ids.includes(g.id) ? 'bg-amber-500/20 text-amber-500 border border-amber-500/30' : 'bg-zinc-800 text-zinc-400 border border-white/5 hover:border-white/10'}`}
                    data-testid={`group-toggle-${g.id}`}>
                    {g.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={v => setForm(f => ({ ...f, active: v }))} data-testid="user-active-switch" />
              <Label className="text-zinc-300">Activo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialogOpen(false)} className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200">Cancelar</Button>
            <Button onClick={handleSave} className="bg-amber-500 hover:bg-amber-600 text-black font-medium" data-testid="user-save-button">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
